/* STH Hat Customizer: live "your logo on this hat" preview.
   Plain JS, no libraries. Mount with STHCustomizer.mount(rootEl, config).
   The engine knows nothing about Shopify. The page passes in the product profile
   (photos + calibration), the patch catalogue and an onAddToCart handler. */
(function () {
  'use strict';

  /* ---------- small helpers ---------- */

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const money = (n) => '$' + n.toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function makeCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not load ' + src));
      img.src = src;
    });
  }

  function rgb(hex) {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  function shade(hex, k) { // k < 1 darkens, > 1 lightens
    const [r, g, b] = rgb(hex);
    const f = (v) => clamp(Math.round(k > 1 ? v + (255 - v) * (k - 1) : v * k), 0, 255);
    return `rgb(${f(r)},${f(g)},${f(b)})`;
  }

  // Seeded random, so a camo patch looks the same every time it's redrawn.
  function seeded(seed) {
    let a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* ---------- textures (built once, reused as patterns) ---------- */

  const tex = {};
  function grainTile() {
    if (tex.grain) return tex.grain;
    const n = 192, c = makeCanvas(n, n), x = c.getContext('2d');
    const rnd = seeded(7), id = x.createImageData(n, n);
    for (let i = 0; i < n * n; i++) {
      const v = 128 + (rnd() - 0.5) * 120;
      id.data[i * 4] = id.data[i * 4 + 1] = id.data[i * 4 + 2] = v;
      id.data[i * 4 + 3] = 255;
    }
    x.putImageData(id, 0, 0);
    // Soften fine noise into a leather-like grain, then add a coarser layer on top.
    const out = makeCanvas(n, n), o = out.getContext('2d');
    o.filter = 'blur(0.7px)';
    o.drawImage(c, 0, 0);
    o.filter = 'blur(2.5px)';
    o.globalAlpha = 0.6;
    o.drawImage(c, 0, 0, n * 2, n * 2);
    tex.grain = out;
    return out;
  }
  function pebbleTile() {
    if (tex.pebble) return tex.pebble;
    const n = 64, c = makeCanvas(n, n), x = c.getContext('2d'), rnd = seeded(11);
    x.fillStyle = '#808080';
    x.fillRect(0, 0, n, n);
    for (let i = 0; i < 70; i++) {
      const px = rnd() * n, py = rnd() * n, r = 2 + rnd() * 2.2;
      const g = x.createRadialGradient(px - r * 0.3, py - r * 0.3, 0, px, py, r);
      g.addColorStop(0, '#b4b4b4');
      g.addColorStop(1, '#5a5a5a');
      x.fillStyle = g;
      x.beginPath();
      x.arc(px, py, r, 0, Math.PI * 2);
      x.fill();
    }
    tex.pebble = c;
    return c;
  }
  function threadTile() {
    if (tex.thread) return tex.thread;
    const n = 6, c = makeCanvas(n, n), x = c.getContext('2d');
    x.fillStyle = '#808080';
    x.fillRect(0, 0, n, n);
    x.strokeStyle = 'rgba(255,255,255,0.75)';
    x.lineWidth = 1.2;
    x.beginPath();
    x.moveTo(-1, n + 1); x.lineTo(n + 1, -1);
    x.moveTo(-1 - n / 2, n / 2 + 1); x.lineTo(n / 2 + 1, -1 - n / 2);
    x.moveTo(n / 2 - 1, n + n / 2 + 1); x.lineTo(n + n / 2 + 1, n / 2 - 1);
    x.stroke();
    x.strokeStyle = 'rgba(0,0,0,0.45)';
    x.lineWidth = 0.8;
    x.beginPath();
    x.moveTo(1, n + 1); x.lineTo(n + 1, 1);
    x.stroke();
    tex.thread = c;
    return c;
  }
  function weaveTile() {
    if (tex.weave) return tex.weave;
    const n = 4, c = makeCanvas(n, n), x = c.getContext('2d');
    x.fillStyle = '#808080';
    x.fillRect(0, 0, n, n);
    x.fillStyle = '#9a9a9a';
    x.fillRect(0, 0, 2, 2); x.fillRect(2, 2, 2, 2);
    x.fillStyle = '#6c6c6c';
    x.fillRect(2, 0, 2, 2); x.fillRect(0, 2, 2, 2);
    tex.weave = c;
    return c;
  }

  // Lay a grey texture tile over whatever is already drawn (grey 128 = no change).
  function overlayTexture(ctx, w, h, tile, alpha, mode, scale) {
    ctx.save();
    ctx.globalCompositeOperation = mode || 'soft-light';
    ctx.globalAlpha = alpha;
    const p = ctx.createPattern(tile, 'repeat');
    if (scale && p.setTransform) p.setTransform(new DOMMatrix().scale(scale));
    ctx.fillStyle = p;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }

  // Real stock photos (camo, carbon fibre, holographic...) used as the patch surface.
  // Loaded once per src; anything waiting gets a callback so it can redraw.
  const photoTex = new Map(), texListeners = new Set();
  function photoTexture(t) {
    let e = photoTex.get(t.src);
    if (!e) {
      e = { img: null };
      photoTex.set(t.src, e);
      loadImage(t.src).then((img) => { e.img = img; texListeners.forEach((cb) => cb()); }).catch(() => {});
    }
    return e.img;
  }
  // The clean strip of the photo, with its contrast and colour lifted: product photos are
  // shot in soft light, which flattens carbon weave and camo at patch size. Done once per strip.
  const stripCache = new Map();
  function photoStrip(img, rect) {
    const key = img.src + '|' + rect.join(',');
    if (stripCache.has(key)) return stripCache.get(key);
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const c = makeCanvas(rect[2] * iw, rect[3] * ih), x = c.getContext('2d');
    x.drawImage(img, rect[0] * iw, rect[1] * ih, c.width, c.height, 0, 0, c.width, c.height);
    const id = x.getImageData(0, 0, c.width, c.height), d = id.data;
    let sum = 0, sq = 0, n = d.length / 4;
    for (let i = 0; i < d.length; i += 4) { const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]; sum += l; sq += l * l; }
    const mean = sum / n, sd = Math.sqrt(Math.max(1, sq / n - mean * mean));
    const gain = clamp(26 / sd, 1, 2.6); // aim for a visible but natural texture
    for (let i = 0; i < d.length; i += 4) {
      const r = d[i], g = d[i + 1], b = d[i + 2];
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      const nl = mean + (l - mean) * gain;
      for (let k = 0; k < 3; k++) {
        const v = d[i + k];
        const sat = l + (v - l) * 1.2; // a touch more colour
        d[i + k] = clamp(sat + (nl - l), 0, 255);
      }
    }
    x.putImageData(id, 0, 0);
    stripCache.set(key, c);
    return c;
  }
  function paintPhoto(ctx, w, h, img, rect) {
    const strip = photoStrip(img, rect);
    const k = Math.max(w / strip.width, h / strip.height);
    const dw = strip.width * k, dh = strip.height * k;
    ctx.drawImage(strip, (w - dw) / 2, (h - dh) / 2, dw, dh);
  }
  // Golf-ball dimples: Lone Star's photo is near-flat white, so draw them. Transparent tile
  // with a soft shadow in each dimple and a light lip on the lower edge, drawn normally
  // (blend modes do nothing on white).
  function dimpleTile() {
    if (tex.dimple) return tex.dimple;
    const n = 30, h = Math.round(n * 1.732), c = makeCanvas(n, h), x = c.getContext('2d');
    const r = n * 0.4;
    const centers = [[0, 0], [n, 0], [n / 2, h / 2], [0, h], [n, h]];
    for (const [cx, cy] of centers) {
      const g = x.createRadialGradient(cx - r * 0.25, cy - r * 0.3, 0, cx, cy, r);
      g.addColorStop(0, 'rgba(0,0,0,0.22)');
      g.addColorStop(0.7, 'rgba(0,0,0,0.08)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g;
      x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.fill();
      x.strokeStyle = 'rgba(255,255,255,0.55)';
      x.lineWidth = 1.2;
      x.beginPath(); x.arc(cx, cy, r * 0.92, 0.15 * Math.PI, 0.85 * Math.PI); x.stroke();
      x.strokeStyle = 'rgba(0,0,0,0.12)';
      x.beginPath(); x.arc(cx, cy, r * 0.92, 1.15 * Math.PI, 1.85 * Math.PI); x.stroke();
    }
    tex.dimple = c;
    return c;
  }

  function paintCamo(ctx, w, h, palette, seed) {
    const rnd = seeded(seed);
    ctx.fillStyle = palette[0];
    ctx.fillRect(0, 0, w, h);
    const unit = Math.max(w, h);
    for (let layer = 1; layer < palette.length; layer++) {
      ctx.fillStyle = palette[layer];
      const blobs = 7 + layer * 3;
      for (let i = 0; i < blobs; i++) {
        const cx = rnd() * w, cy = rnd() * h;
        const r = unit * (0.06 + rnd() * 0.12) / (layer * 0.6 + 0.6);
        ctx.beginPath();
        const pts = 9;
        for (let k = 0; k <= pts; k++) {
          const a = (k / pts) * Math.PI * 2;
          const rr = r * (0.55 + rnd() * 0.75);
          const px = cx + Math.cos(a) * rr * 1.5, py = cy + Math.sin(a) * rr;
          if (k === 0) ctx.moveTo(px, py); else ctx.quadraticCurveTo(cx + Math.cos(a - 0.35) * rr * 1.9, cy + Math.sin(a - 0.35) * rr * 1.2, px, py);
        }
        ctx.fill();
      }
    }
  }

  /* ---------- artwork prep ---------- */

  const PREVIEWABLE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

  // Draw the uploaded image into a canvas no bigger than 1400px on its longest side.
  function artToCanvas(img) {
    let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    if (!w || !h) { w = 1000; h = 1000; } // SVG with no size set
    const k = Math.min(1, 1400 / Math.max(w, h));
    const c = makeCanvas(w * k, h * k);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c;
  }

  // Remove a flat backdrop (white box behind a logo, etc.) by flood-filling inward from
  // the edges. Leaves photos and art that already has transparency alone.
  // opts.holes also clears backdrop-coloured pixels the fill can't reach (inside an A, D or 9).
  // Off by default: white lettering inside a dark badge is enclosed too, and that white must stay.
  function removeBackground(src, opts) {
    const w = src.width, h = src.height;
    const c = makeCanvas(w, h), x = c.getContext('2d');
    x.drawImage(src, 0, 0);
    let id;
    try { id = x.getImageData(0, 0, w, h); } catch (e) { return { canvas: src, removed: false }; }
    const d = id.data;

    let see = 0, clear = 0;
    for (let i = 3; i < d.length; i += 4 * 29) { see++; if (d[i] < 128) clear++; }
    if (clear / see > 0.05) return { canvas: src, removed: false };

    const edge = [];
    for (let i = 0; i < w; i++) edge.push(i, (h - 1) * w + i);
    for (let j = 0; j < h; j++) edge.push(j * w, j * w + w - 1);
    // The backdrop is the most common colour around the border. Logos often touch the
    // edge of the image (a circle badge cropped tight), so don't expect the whole border
    // to match, just most of it. A photo has no dominant border colour and is left alone.
    const buckets = new Map();
    for (const p of edge) {
      const k = (d[p * 4] >> 4) << 8 | (d[p * 4 + 1] >> 4) << 4 | (d[p * 4 + 2] >> 4);
      buckets.set(k, (buckets.get(k) || 0) + 1);
    }
    let topKey = 0, topN = 0;
    for (const [k, n] of buckets) if (n > topN) { topN = n; topKey = k; }
    const kr = ((topKey >> 8) & 15) * 16 + 8, kg = ((topKey >> 4) & 15) * 16 + 8, kb = (topKey & 15) * 16 + 8;
    let r0 = 0, g0 = 0, b0 = 0, m = 0;
    for (const p of edge) {
      const dr = d[p * 4] - kr, dg = d[p * 4 + 1] - kg, db = d[p * 4 + 2] - kb;
      if (dr * dr + dg * dg + db * db <= 40 * 40) { r0 += d[p * 4]; g0 += d[p * 4 + 1]; b0 += d[p * 4 + 2]; m++; }
    }
    if (m / edge.length < 0.5) return { canvas: src, removed: false };
    r0 /= m; g0 /= m; b0 /= m;

    const tol = 60 * 60, soft = 110 * 110;
    const dist = (p) => {
      const dr = d[p * 4] - r0, dg = d[p * 4 + 1] - g0, db = d[p * 4 + 2] - b0;
      return dr * dr + dg * dg + db * db;
    };
    const seen = new Uint8Array(w * h), stack = [];
    for (const p of edge) if (!seen[p] && dist(p) <= tol) { seen[p] = 1; stack.push(p); }
    let cleared = 0;
    while (stack.length) {
      const p = stack.pop();
      d[p * 4 + 3] = 0;
      cleared++;
      const px = p % w, py = (p / w) | 0;
      const next = [px > 0 ? p - 1 : -1, px < w - 1 ? p + 1 : -1, py > 0 ? p - w : -1, py < h - 1 ? p + w : -1];
      for (const q of next) {
        if (q < 0 || seen[q]) continue;
        const dq = dist(q);
        if (dq <= tol) { seen[q] = 1; stack.push(q); }
        else if (dq <= soft) { seen[q] = 1; d[q * 4 + 3] = Math.round(255 * (dq - tol) / (soft - tol)); } // anti-alias the edge
      }
    }
    if (cleared / (w * h) < 0.02) return { canvas: src, removed: false };
    if (opts && opts.holes) {
      // Tighter than the edge fill so light-grey detail in the art survives.
      const tolH = 45 * 45, softH = 90 * 90;
      for (let p = 0; p < w * h; p++) {
        if (seen[p]) continue;
        const dq = dist(p);
        if (dq <= tolH) d[p * 4 + 3] = 0;
        else if (dq <= softH) d[p * 4 + 3] = Math.min(d[p * 4 + 3], Math.round(255 * (dq - tolH) / (softH - tolH)));
      }
    }
    x.putImageData(id, 0, 0);
    return { canvas: c, removed: true };
  }

  // Crop to the visible pixels so "fit inside the patch" means the logo, not its empty margin.
  function trim(src) {
    const w = src.width, h = src.height;
    const d = src.getContext('2d').getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] > 16) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) return src;
    const c = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
    c.getContext('2d').drawImage(src, -x0, -y0);
    return c;
  }

  // Which pixels does the laser burn? For a logo on a transparent background that's every
  // visible pixel. For a two-tone logo (black badge with white lettering) it's the darker
  // tone, split with Otsu's threshold. "invert" swaps the two.
  function engraveMask(art, invert) {
    const w = art.width, h = art.height;
    const src = art.getContext('2d').getImageData(0, 0, w, h).data;
    const hist = new Array(256).fill(0);
    let n = 0;
    for (let i = 0; i < src.length; i += 4) {
      if (src[i + 3] < 128) continue;
      const l = Math.round(0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2]);
      hist[l]++; n++;
    }
    let split = false, t = 128;
    if (n) {
      let sum = 0;
      for (let i = 0; i < 256; i++) sum += i * hist[i];
      let sB = 0, wB = 0, best = 0;
      for (let i = 0; i < 256; i++) {
        wB += hist[i];
        if (!wB) continue;
        const wF = n - wB;
        if (!wF) break;
        sB += i * hist[i];
        const mB = sB / wB, mF = (sum - sB) / wF;
        const between = wB * wF * (mB - mF) * (mB - mF);
        if (between > best) { best = between; t = i; }
      }
      let lo = 0, hi = 0, nlo = 0, nhi = 0;
      for (let i = 0; i < 256; i++) { if (i <= t) { lo += i * hist[i]; nlo += hist[i]; } else { hi += i * hist[i]; nhi += hist[i]; } }
      split = nlo / n > 0.06 && nhi / n > 0.06 && (hi / (nhi || 1)) - (lo / (nlo || 1)) > 70;
    }
    const out = makeCanvas(w, h), ox = out.getContext('2d'), od = ox.createImageData(w, h);
    for (let i = 0; i < src.length; i += 4) {
      const a = src[i + 3];
      let ink = a;
      if (split && a >= 16) {
        const l = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2];
        const dark = l <= t;
        ink = (invert ? !dark : dark) ? a : 0;
      }
      od.data[i] = od.data[i + 1] = od.data[i + 2] = 255;
      od.data[i + 3] = ink;
    }
    ox.putImageData(od, 0, 0);
    return { canvas: out, split };
  }

  // Fewer, flatter colours: reads more like thread than a photo print.
  function posterize(art, step) {
    const c = makeCanvas(art.width, art.height), x = c.getContext('2d');
    x.drawImage(art, 0, 0);
    const id = x.getImageData(0, 0, c.width, c.height), d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = Math.round(d[i] / step) * step;
      d[i + 1] = Math.round(d[i + 1] / step) * step;
      d[i + 2] = Math.round(d[i + 2] / step) * step;
      d[i + 3] = d[i + 3] > 110 ? 255 : 0;
    }
    x.putImageData(id, 0, 0);
    return c;
  }

  // Placeholder art shown before the customer uploads anything.
  function sampleArt(line1, line2) {
    const c = makeCanvas(900, 520), x = c.getContext('2d');
    x.fillStyle = '#000';
    x.beginPath(); // a little mountain range
    x.moveTo(180, 250); x.lineTo(330, 70); x.lineTo(410, 165); x.lineTo(480, 95); x.lineTo(720, 250);
    x.lineTo(650, 250); x.lineTo(480, 140); x.lineTo(420, 215); x.lineTo(330, 120); x.lineTo(240, 250);
    x.closePath();
    x.fill();
    x.textAlign = 'center';
    x.textBaseline = 'alphabetic';
    x.font = '800 150px Poppins, Arial Black, sans-serif';
    x.fillText(line1, 450, 410);
    x.font = '600 66px Poppins, Arial, sans-serif';
    x.fillText(line2, 450, 500);
    return trim(c);
  }

  /* ---------- patch shapes ---------- */

  function shapePath(ctx, id, x, y, w, h) {
    ctx.beginPath();
    if (id === 'circle' || id === 'oval') {
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else if (id === 'hex') { // wide hexagon, points left and right
      const k = w * 0.2;
      ctx.moveTo(x + k, y); ctx.lineTo(x + w - k, y); ctx.lineTo(x + w, y + h / 2);
      ctx.lineTo(x + w - k, y + h); ctx.lineTo(x + k, y + h); ctx.lineTo(x, y + h / 2);
      ctx.closePath();
    } else {
      const r = Math.min(w, h) * (id === 'rect' ? 0.07 : 0.2); // 'rounded' and 'roundsq' share this
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }
  }

  // Morphology on alpha masks (same-size canvases). grow = dilate by r px; shrink = erode.
  function grow(src, r) {
    const c = makeCanvas(src.width, src.height), x = c.getContext('2d');
    const steps = 28;
    for (let ring = 1; ring <= 3; ring++) {
      const rr = r * ring / 3;
      for (let i = 0; i < steps; i++) {
        const a = (i / steps) * Math.PI * 2;
        x.drawImage(src, Math.cos(a) * rr, Math.sin(a) * rr);
      }
    }
    x.drawImage(src, 0, 0);
    return c;
  }
  function invert(src) {
    const c = makeCanvas(src.width, src.height), x = c.getContext('2d');
    x.fillStyle = '#fff';
    x.fillRect(0, 0, c.width, c.height);
    x.globalCompositeOperation = 'destination-out';
    x.drawImage(src, 0, 0);
    return c;
  }
  const shrink = (src, r) => invert(grow(invert(src), r));
  function harden(src, blur) { // round off the jaggies, then a crisp edge again
    const s = makeCanvas(src.width, src.height), sx = s.getContext('2d');
    sx.filter = `blur(${Math.max(1, blur)}px)`;
    sx.drawImage(src, 0, 0);
    const id = sx.getImageData(0, 0, s.width, s.height), d = id.data;
    for (let i = 3; i < d.length; i += 4) d[i] = clamp((d[i] - 90) * 4, 0, 255);
    sx.putImageData(id, 0, 0);
    return s;
  }

  // Cut line of a die-cut patch: always ONE piece (Ryan: never separate pieces).
  // The logo is closed (grow then shrink) to bridge the gaps between its parts, grown by the
  // 0.1in border, and its enclosed holes filled. Parts too far apart to bridge cleanly are
  // wrapped in their convex hull, which makes one sturdy piece.
  function dieCut(art, W, H, box, pad, bridge) {
    bridge = bridge || Math.max(pad, 2); // gap-joining reach; independent of the border so 0in still joins
    const base = makeCanvas(W, H);
    base.getContext('2d').drawImage(art, box.x, box.y, box.w, box.h);
    let result = null;
    for (const mult of [1.6]) { // small bridge only (letter spacing); wider ones leave thin, fragile necks
      const R = bridge * mult, M = Math.ceil(R) + 2;
      const big = makeCanvas(W + M * 2, H + M * 2);
      big.getContext('2d').drawImage(base, M, M);
      const closed = shrink(grow(big, R), R);
      const m = makeCanvas(W, H);
      m.getContext('2d').drawImage(closed, -M, -M);
      result = fillHoles(harden(pad > 0 ? grow(m, pad) : m, Math.max(pad, bridge * 0.5) * 0.45));
      if (countPieces(result) <= 1) return result;
    }
    return fillHoles(hullOf(result));
  }

  // Convex hull of a mask's opaque pixels, filled.
  function hullOf(mask) {
    const w = mask.width, h = mask.height, d = mask.getContext('2d').getImageData(0, 0, w, h).data;
    const pts = [];
    const step = Math.max(1, Math.round(Math.max(w, h) / 300));
    for (let y = 0; y < h; y += step) {
      let l = -1, r = -1;
      for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 128) { if (l < 0) l = x; r = x; }
      if (l >= 0) pts.push([l, y], [r, y]);
    }
    pts.sort((p, q) => p[0] - q[0] || p[1] - q[1]);
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [], upper = [];
    for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
    for (const p of pts.slice().reverse()) { while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
    const hull = lower.slice(0, -1).concat(upper.slice(0, -1));
    const c = makeCanvas(w, h), x = c.getContext('2d');
    x.drawImage(mask, 0, 0);
    x.fillStyle = '#fff';
    x.beginPath();
    hull.forEach((p, i) => (i ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1])));
    x.closePath();
    x.fill();
    return c;
  }

  // Fill any gap fully enclosed by the patch (inside a mountain peak, the middle of an O):
  // a die-cut patch has one outer cut line per piece, no interior cut-outs.
  function fillHoles(mask) {
    const w = mask.width, h = mask.height, x = mask.getContext('2d');
    const id = x.getImageData(0, 0, w, h), d = id.data;
    const out = new Uint8Array(w * h), stack = [];
    const open = (p) => d[p * 4 + 3] < 128;
    for (let i = 0; i < w; i++) { stack.push(i, (h - 1) * w + i); }
    for (let j = 0; j < h; j++) { stack.push(j * w, j * w + w - 1); }
    while (stack.length) {
      const p = stack.pop();
      if (out[p] || !open(p)) continue;
      out[p] = 1;
      const px = p % w, py = (p / w) | 0;
      if (px > 0) stack.push(p - 1);
      if (px < w - 1) stack.push(p + 1);
      if (py > 0) stack.push(p - w);
      if (py < h - 1) stack.push(p + w);
    }
    for (let p = 0; p < w * h; p++) if (!out[p]) d[p * 4 + 3] = 255;
    x.putImageData(id, 0, 0);
    return mask;
  }

  // How many separate pieces a cut mask makes (ignores specks under 0.5% of the area).
  function countPieces(mask) {
    const k = Math.min(1, 160 / Math.max(mask.width, mask.height));
    const w = Math.max(1, Math.round(mask.width * k)), h = Math.max(1, Math.round(mask.height * k));
    const c = makeCanvas(w, h), x = c.getContext('2d');
    x.drawImage(mask, 0, 0, w, h);
    const d = x.getImageData(0, 0, w, h).data, seen = new Uint8Array(w * h);
    let total = 0;
    for (let i = 0; i < w * h; i++) if (d[i * 4 + 3] > 128) total++;
    let pieces = 0;
    for (let i = 0; i < w * h; i++) {
      if (seen[i] || d[i * 4 + 3] <= 128) continue;
      let size = 0; const stack = [i]; seen[i] = 1;
      while (stack.length) {
        const p = stack.pop(); size++;
        const px = p % w, py = (p / w) | 0;
        for (const q of [px > 0 ? p - 1 : -1, px < w - 1 ? p + 1 : -1, py > 0 ? p - w : -1, py < h - 1 ? p + w : -1]) {
          if (q >= 0 && !seen[q] && d[q * 4 + 3] > 128) { seen[q] = 1; stack.push(q); }
        }
      }
      if (size > total * 0.005) pieces++;
    }
    return pieces;
  }

  // A canvas that is opaque where the patch is and clear elsewhere.
  function shapeMask(shape, W, H, art, box, pad, bridge) {
    if (shape === 'diecut') return dieCut(art, W, H, box, pad, bridge);
    const c = makeCanvas(W, H), x = c.getContext('2d');
    x.fillStyle = '#fff';
    shapePath(x, shape, 0, 0, W, H);
    x.fill();
    return c;
  }

  // Darken just inside the patch edge so the cut reads as a real edge.
  function edgeShade(ctx, mask, W, H, width, alpha) {
    const inv = makeCanvas(W, H), x = inv.getContext('2d');
    x.fillStyle = '#000';
    x.fillRect(0, 0, W, H);
    x.globalCompositeOperation = 'destination-out';
    x.drawImage(mask, 0, 0);
    const b = makeCanvas(W, H), bx = b.getContext('2d');
    bx.filter = `blur(${width}px)`;
    bx.drawImage(inv, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    ctx.globalAlpha = alpha;
    ctx.drawImage(b, 0, 0);
    ctx.restore();
  }

  function lightGradient(ctx, W, H, top, bottom) {
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    const g = ctx.createLinearGradient(0, 0, W * 0.3, H);
    g.addColorStop(0, `rgba(255,255,255,${top})`);
    g.addColorStop(0.55, 'rgba(255,255,255,0)');
    g.addColorStop(1, `rgba(0,0,0,${bottom})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  // Stitched merrow border for fabric patches (path shapes only).
  function merrow(ctx, shape, W, H, color) {
    if (shape === 'diecut') return;
    const lw = Math.max(3, Math.min(W, H) * 0.075);
    ctx.save();
    shapePath(ctx, shape, lw / 2, lw / 2, W - lw, H - lw);
    ctx.lineWidth = lw;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.setLineDash([Math.max(1, lw * 0.12), Math.max(1.5, lw * 0.16)]);
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    shapePath(ctx, shape, lw, lw, W - lw * 2, H - lw * 2);
    ctx.stroke();
    ctx.restore();
  }

  /* ---------- patch looks ---------- */

  // Builds the flat patch at exactly W x H pixels. The hat render bends and shades it.
  function buildPatch(o) {
    const { look, shape, W, H } = o;
    const out = makeCanvas(W, H), ctx = out.getContext('2d');
    const box = o.box;

    if (look === 'embroidery') { // thread straight on the hat, no patch
      const art = posterize(o.art, 12);
      ctx.drawImage(art, box.x, box.y, box.w, box.h);
      ctx.save();
      ctx.globalCompositeOperation = 'source-atop';
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = ctx.createPattern(threadTile(), 'repeat');
      ctx.globalCompositeOperation = 'soft-light';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
      ctx.save();
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(art, box.x, box.y, box.w, box.h);
      ctx.restore();
      lightGradient(ctx, W, H, 0.22, 0.16);
      return out;
    }

    const mask = shapeMask(shape, W, H, o.art, box, o.pad, o.bridge); // die-cut follows the whole logo, not just the engraved part

    if (look === 'leather') {
      const c = o.color;
      const golf = /golf/i.test(c.name || '') || c.pattern === 'golf';
      const photo = !golf && c.texture && photoTexture(c.texture);
      ctx.fillStyle = c.surface;
      ctx.fillRect(0, 0, W, H);
      if (photo) paintPhoto(ctx, W, H, photo, c.texture.rect);
      else if (c.pattern === 'camo') paintCamo(ctx, W, H, c.palette, 3);
      if (c.pattern === 'pebble') overlayTexture(ctx, W, H, pebbleTile(), 0.9, 'overlay', Math.max(0.6, W / 300));
      if (golf) overlayTexture(ctx, W, H, dimpleTile(), 1, 'source-over', Math.max(0.35, W / 900));
      // Light grain only: strong soft-light noise pulls saturated colours toward grey.
      if (!photo) overlayTexture(ctx, W, H, grainTile(), 0.16, 'soft-light', Math.max(0.5, W / 380));

      // What the laser burns. Normal: the logo. Reversed: the field around the logo,
      // inside a thin unburned border, so the logo stands up in bare leather.
      const burn = makeCanvas(W, H), bx = burn.getContext('2d');
      if (o.reverse) {
        bx.fillStyle = '#fff';
        if (shape === 'diecut') bx.drawImage(mask, 0, 0);
        else {
          const m = Math.max(2, Math.min(W, H) * 0.065);
          shapePath(bx, shape, m, m, W - m * 2, H - m * 2);
          bx.fill();
        }
        bx.globalCompositeOperation = 'destination-out';
        bx.drawImage(o.mask, box.x, box.y, box.w, box.h);
      } else {
        bx.drawImage(o.mask, box.x, box.y, box.w, box.h);
      }

      // The burned area: engrave colour + grain, with a thin shadow along its top edges so
      // it reads as sunk into the surface.
      const ink = makeCanvas(W, H), ix = ink.getContext('2d');
      ix.drawImage(burn, 0, 0);
      ix.globalCompositeOperation = 'source-in';
      if (c.metallic) {
        const g = ix.createLinearGradient(0, box.y, 0, box.y + box.h);
        g.addColorStop(0, shade(c.engrave, 1.35));
        g.addColorStop(0.5, c.engrave);
        g.addColorStop(1, shade(c.engrave, 0.7));
        ix.fillStyle = g;
      } else {
        ix.fillStyle = c.engrave;
      }
      ix.fillRect(0, 0, W, H);
      overlayTexture(ix, W, H, grainTile(), c.metallic ? 0.25 : 0.55, 'soft-light', Math.max(0.5, W / 380));
      // Blend modes paint into transparent pixels too: clip the grain back to the burn,
      // or it lays a grey film over the whole patch.
      ix.globalCompositeOperation = 'destination-in';
      ix.drawImage(burn, 0, 0);
      const lip = Math.max(1, W / 260);
      const rim = makeCanvas(W, H), rx = rim.getContext('2d');
      rx.drawImage(burn, 0, 0);
      rx.globalCompositeOperation = 'source-in';
      rx.fillStyle = '#000';
      rx.fillRect(0, 0, W, H);
      rx.globalCompositeOperation = 'destination-out';
      rx.drawImage(burn, 0, lip);
      ix.globalCompositeOperation = 'source-atop';
      ix.globalAlpha = 0.55;
      ix.drawImage(rim, 0, 0);
      ctx.drawImage(ink, 0, 0);

      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(mask, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      edgeShade(ctx, mask, W, H, Math.max(1.5, W / 140), 0.4);
      lightGradient(ctx, W, H, 0.1, 0.07);
      return out;
    }

    // UV printed, sublimated and embroidered patches: printed / stitched art on a patch.
    ctx.fillStyle = o.base || '#ffffff';
    ctx.fillRect(0, 0, W, H);
    if (look === 'uv') overlayTexture(ctx, W, H, grainTile(), 0.14, 'soft-light', Math.max(0.5, W / 380));
    if (look === 'sublimated') overlayTexture(ctx, W, H, weaveTile(), 0.35, 'soft-light');
    if (look === 'embpatch') {
      overlayTexture(ctx, W, H, weaveTile(), 0.5, 'soft-light');
      const art = posterize(o.art, 12);
      const t = makeCanvas(W, H), tx = t.getContext('2d');
      tx.drawImage(art, box.x, box.y, box.w, box.h);
      tx.globalCompositeOperation = 'source-atop';
      tx.globalAlpha = 0.6;
      tx.fillStyle = tx.createPattern(threadTile(), 'repeat');
      tx.globalCompositeOperation = 'soft-light';
      tx.fillRect(0, 0, W, H);
      tx.globalCompositeOperation = 'destination-in';
      tx.globalAlpha = 1;
      tx.drawImage(art, box.x, box.y, box.w, box.h);
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.35)';
      ctx.shadowBlur = Math.max(1, W / 200);
      ctx.shadowOffsetY = Math.max(1, W / 300);
      ctx.drawImage(t, 0, 0);
      ctx.restore();
    } else {
      ctx.drawImage(o.art, box.x, box.y, box.w, box.h);
    }
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    if (look === 'sublimated' || look === 'embpatch') merrow(ctx, shape, W, H, o.border || '#1c1c1c');
    edgeShade(ctx, mask, W, H, Math.max(1.5, W / 140), look === 'uv' ? 0.3 : 0.4);
    lightGradient(ctx, W, H, look === 'uv' ? 0.22 : 0.12, 0.1);
    return out;
  }

  /* ---------- drawing the patch onto the hat ---------- */

  // Wrap the flat patch around the crown, treated as a vertical cylinder of radius R
  // (px). Strips squeeze as the surface turns away. yaw turns the whole patch when it
  // sits off-center, and drop curves the strips down a little toward the edges.
  function drawWrapped(ctx, img, cx, cy, rot, wrap) {
    const w = img.width, h = img.height;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    if (!wrap || !wrap.R) { ctx.drawImage(img, -w / 2, -h / 2); ctx.restore(); return; }
    const R = wrap.R, yaw = wrap.yaw || 0, drop = wrap.drop || 0;
    const theta = Math.min(1.3, (w / 2) / R); // half the arc the patch covers, in radians
    const n = clamp(Math.round(w / 3), 24, 180);
    const sx = (t) => R * (Math.sin(t * theta + yaw) - Math.sin(yaw));
    for (let i = 0; i < n; i++) {
      const t0 = (i / n) * 2 - 1, t1 = ((i + 1) / n) * 2 - 1, tm = (t0 + t1) / 2;
      const dx0 = sx(t0), dx1 = sx(t1);
      if (dx1 <= dx0) continue; // turned fully away
      const dy = drop * tm * tm * h * 0.9;
      const squash = 1 - (1 - Math.cos(tm * theta + yaw)) * 0.22;
      const dh = h * squash;
      ctx.drawImage(img, (i / n) * w, 0, w / n, h, dx0, -h / 2 + dy + (h - dh) / 2, dx1 - dx0 + 0.6, dh);
    }
    ctx.restore();
  }

  function silhouette(img, color) {
    const c = makeCanvas(img.width, img.height), x = c.getContext('2d');
    x.drawImage(img, 0, 0);
    x.globalCompositeOperation = 'source-in';
    x.fillStyle = color;
    x.fillRect(0, 0, c.width, c.height);
    return c;
  }

  /* ---------- the component ---------- */

  function mount(root, cfg) {
    const P = cfg.product, C = P.calib;
    if (!P.colors || !P.colors.length) {
      root.innerHTML = `<p class="sthc-fallback">The hat preview isn't available for this hat right now. <a href="/pages/contact">Contact us</a> and we'll set up your order and send a free mockup.</p>`;
      return null;
    }
    const S = {
      colorIdx: 0,
      decoIdx: 0,
      patchIdx: 0,
      shape: cfg.shapes[0].id,
      wIn: cfg.shapes[0].defaultIn,
      x: C.x, y: C.y, rot: C.rot || 0,
      preset: P.presets[0].name,
      reverse: false,
      borderIn: null,
      uvBaseIdx: 0,
      removeBg: true,
      raw: null,          // uploaded art as canvas (before background removal)
      art: null,          // art used for drawing
      mask: null,         // engrave mask
      sample: sampleArt('YOUR', 'LOGO HERE'),
      artVersion: 0,
      file: null,
      fileName: '',
      previewable: true,
      upload: null,       // { state: 'idle'|'uploading'|'done'|'error', url, error }
      emailLater: false,
      qty: 1,
      hat: null,
    };

    const deco = () => P.decorations[S.decoIdx];
    const patchColor = () => cfg.patchColors[S.patchIdx];
    const shapeDef = () => cfg.shapes.find((s) => s.id === S.shape);
    // Some decorations only come in certain shapes (stitched edges need simple outlines).
    const shapeOk = (id, d) => !(d || deco()).shapes || (d || deco()).shapes.includes(id);
    function fixShape() {
      if (shapeOk(S.shape)) return;
      const first = cfg.shapes.find((sh) => shapeOk(sh.id));
      if (first) { S.shape = first.id; S.borderIn = null; S.wIn = fitSize(first.defaultIn); }
    }

    root.classList.add('sthc');
    root.innerHTML = `
      <div class="sthc-stage">
        <div class="sthc-canvas-wrap">
          <canvas class="sthc-canvas" width="1000" height="1000" aria-label="Hat preview"></canvas>
          <div class="sthc-loading" hidden>Loading hat…</div>
          <p class="sthc-pill">Drag the patch to move it · scroll or pinch to resize</p>
        </div>
        <p class="sthc-approx" hidden>Preview is approximate for embroidery. We send a proof before stitching.</p>
        <div class="sthc-stage-actions">
          <button type="button" class="sthc-link" data-download>Download mockup</button>
          <button type="button" class="sthc-link" data-reset>Reset placement</button>
        </div>
      </div>
      <div class="sthc-panel">
        <div class="sthc-group">
          <p class="sthc-label">Hat color <span data-color-name></span></p>
          <div class="sthc-swatches sthc-hats" data-colors></div>
        </div>
        <div class="sthc-group">
          <p class="sthc-label">Decoration</p>
          <div class="sthc-chips" data-decos></div>
        </div>
        <div class="sthc-group" data-patch-group>
          <p class="sthc-label">Leather patch color <span data-patch-name></span></p>
          <div class="sthc-swatches" data-patches></div>
        </div>
        <div class="sthc-group" data-uvbase-group>
          <p class="sthc-label">Patch material</p>
          <div class="sthc-chips" data-uvbases></div>
        </div>
        <div class="sthc-group" data-shape-group>
          <p class="sthc-label">Patch shape</p>
          <div class="sthc-chips" data-shapes></div>
          <p class="sthc-note" data-shape-note hidden></p>
        </div>
        <div class="sthc-group">
          <p class="sthc-label">Your logo</p>
          <label class="sthc-drop" data-drop>
            <input type="file" class="sthc-file" data-file accept="${esc(cfg.accept)}">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>
            <span><strong data-drop-title>Drop your logo here or choose a file</strong>
            <small data-drop-sub>PNG with a clear background works best. JPG, SVG, PDF, AI and EPS are fine too.</small></span>
          </label>
          <p class="sthc-status" data-status hidden></p>
          <label class="sthc-check" data-bg-row hidden><input type="checkbox" data-removebg checked> Remove the background behind my logo</label>
          <label class="sthc-check" data-invert-row hidden><input type="checkbox" data-invert> <span><strong>Reverse engraving</strong>: burn the background and leave my logo in bare leather</span></label>
          <label class="sthc-check"><input type="checkbox" data-later> I'll email my logo after ordering</label>
        </div>
        <div class="sthc-group">
          <p class="sthc-label">Placement</p>
          <div class="sthc-chips" data-presets></div>
          <div class="sthc-slider"><span>Size</span><input type="range" data-size step="0.05"><b data-size-val></b></div>
          <p class="sthc-note" data-size-note hidden></p>
          <div class="sthc-slider" data-border-row><span>Border</span><input type="range" data-border min="0" max="0.5" step="0.01"><b data-border-val></b></div>
          <p class="sthc-note" data-border-note>Space between your logo and the edge of the patch.</p>
          <div class="sthc-slider"><span>Across</span><input type="range" data-x min="20" max="80" step="0.5"><b data-x-val></b></div>
          <div class="sthc-slider"><span>Up / down</span><input type="range" data-y min="${C.yMin ?? 25}" max="${C.yMax ?? 70}" step="0.5"><b data-y-val></b></div>
          <div class="sthc-slider"><span>Rotate</span><input type="range" data-rot min="-20" max="20" step="1"><b data-rot-val></b></div>
        </div>
        <div class="sthc-group" data-place-group>
          <p class="sthc-label">Additional placements</p>
          <div class="sthc-checks" data-places></div>
          <p class="sthc-note">Want your logo in more spots? Tick them and we'll confirm the price with your proof before we start.</p>
        </div>
        <div class="sthc-group sthc-buy">
          <div class="sthc-qty">
            <button type="button" data-step="-1" aria-label="One less">−</button>
            <input type="number" min="1" max="999" value="1" data-qty aria-label="Quantity">
            <button type="button" data-step="1" aria-label="One more">+</button>
          </div>
          <div class="sthc-price"><strong data-total></strong><small data-each></small></div>
          <div class="sthc-fees" data-fees hidden>
            <p data-setup-line></p>
            <label class="sthc-check"><input type="checkbox" data-reorder> This is a reorder of a logo you've embroidered for me before (no setup fee)</label>
          </div>
          <p class="sthc-note sthc-full" data-min-note hidden></p>
          <p class="sthc-note sthc-full" data-next-break></p>
          <button type="button" class="sthc-btn" data-add>Add to cart</button>
          <p class="sthc-bulk" data-bulk></p>
          <p class="sthc-error" data-error hidden></p>
        </div>
      </div>`;

    const $ = (s) => root.querySelector(s);
    const canvas = $('.sthc-canvas'), ctx = canvas.getContext('2d');

    /* --- build the option lists --- */

    // A listing that is a single hat colour doesn't need a colour picker.
    $('[data-colors]').closest('.sthc-group').hidden = P.colors.length < 2;
    $('[data-colors]').innerHTML = P.colors.map((c, i) => `
      <button type="button" class="sthc-hat" data-color="${i}" title="${esc(c.name)}" aria-label="${esc(c.name)}">
        <img src="${esc(P.photo.thumb.replace('{id}', c.id))}" alt="" loading="lazy" crossorigin="anonymous">
      </button>`).join('');
    $('[data-decos]').innerHTML = P.decorations.map((d, i) =>
      `<button type="button" class="sthc-chip" data-deco="${i}">${esc(d.name)}</button>`).join('');
    $('[data-patches]').innerHTML = cfg.patchColors.map((c, i) => {
      let bg;
      if (c.pattern === 'camo') {
        const t = makeCanvas(60, 60);
        paintCamo(t.getContext('2d'), 60, 60, c.palette, 3);
        bg = `url(${t.toDataURL()}) center/cover`;
      } else if (c.metallic) {
        bg = `linear-gradient(135deg, ${c.surface} 50%, ${c.engrave} 50%)`;
      } else {
        bg = c.surface;
      }
      return `<button type="button" class="sthc-swatch" data-patch="${i}" title="${esc(c.name)}" aria-label="${esc(c.name)}" style="background:${bg}"></button>`;
    }).join('');
    // Textured stock (camo, carbon fibre, holographic, golf ball...) shows the real material in
    // its swatch: a crop of the same photo the patch uses, filled in once the photo has loaded.
    function paintTextureSwatches() {
      root.querySelectorAll('[data-patch]').forEach((btn) => {
        const c = cfg.patchColors[+btn.dataset.patch];
        if (!c || btn.dataset.textured) return;
        const golf = /golf/i.test(c.name || '');
        if (!golf && !c.texture) return;
        const t = makeCanvas(80, 80), x = t.getContext('2d');
        x.fillStyle = c.surface;
        x.fillRect(0, 0, 80, 80);
        if (golf) {
          overlayTexture(x, 80, 80, dimpleTile(), 1, 'source-over', 0.7);
        } else {
          const img = photoTexture(c.texture);
          if (!img) return; // not loaded yet; the texture listener calls this again
          paintPhoto(x, 80, 80, img, c.texture.rect);
        }
        btn.style.background = `url(${t.toDataURL()}) center/cover`;
        btn.dataset.textured = '1';
      });
    }
    paintTextureSwatches();
    texListeners.add(paintTextureSwatches);
    $('[data-uvbases]').innerHTML = cfg.uvBases.map((b, i) =>
      `<button type="button" class="sthc-chip" data-uvbase="${i}"><span class="sthc-dot" style="background:${b.hex}"></span>${esc(b.name)}</button>`).join('');
    $('[data-places]').innerHTML = (P.placements || []).map((t, i) =>
      `<label class="sthc-check"><input type="checkbox" data-place="${i}"> ${esc(t)}</label>`).join('');
    $('[data-place-group]').hidden = !(P.placements || []).length;
    $('[data-shapes]').innerHTML = cfg.shapes.map((s) =>
      `<button type="button" class="sthc-chip" data-shape="${s.id}">${esc(s.label)}</button>`).join('');
    $('[data-presets]').innerHTML = P.presets.map((p) =>
      `<button type="button" class="sthc-chip" data-preset="${esc(p.name)}">${esc(p.name)}</button>`).join('');

    const size = $('[data-size]');
    

    /* --- artwork --- */

    function currentArt() { return S.art || S.sample; }

    function processArt() {
      if (!S.raw) { S.art = null; S.mask = null; return; }
      const r = S.removeBg ? removeBackground(S.raw) : { canvas: S.raw, removed: false };
      S.art = trim(r.canvas);
      S.bgRemoved = r.removed;
      const m = engraveMask(S.art, S.reverse);
      S.mask = m.canvas;
      S.twoTone = m.split;
    }

    let sampleMask = null;
    function maskFor() {
      if (S.mask) return S.mask;
      if (!sampleMask) sampleMask = engraveMask(S.sample, false).canvas;
      return sampleMask;
    }

    /* --- geometry --- */

    // Border = the patch material between the logo and the cut edge, in real inches
    // (the "cut offset"). Each shape starts from its own default; the customer can change it.
    const BORDER_DEFAULT = { diecut: 0.1, rect: 0.15, rounded: 0.18, roundsq: 0.18, circle: 0.12, oval: 0.15, hex: 0.15 };
    function borderIn() { return S.borderIn != null ? S.borderIn : (BORDER_DEFAULT[S.shape] != null ? BORDER_DEFAULT[S.shape] : 0.15); }

    // Patch size in inches. wIn is always the finished patch width. Die-cut height follows
    // the logo's proportions plus the border on both sides.
    function patchInches(w) {
      w = w == null ? S.wIn : w;
      const sd = shapeDef(), art = currentArt(), ar = art.height / art.width;
      if (deco().look === 'embroidery') return { w, h: w * ar };
      if (sd.id === 'diecut') { const b = borderIn(); return { w, h: Math.max(0, w - 2 * b) * ar + 2 * b }; }
      return { w, h: w * sd.aspect };
    }
    function aspect() { const p = patchInches(); return p.h / p.w; } // height / width of the finished patch

    // The logo's visible pixels as points in its own box (-0.5..0.5), so round logos can be
    // fitted inside round patches by their real outline, not their square bounding box.
    let artPts = { key: null, pts: [] };
    function artPoints() {
      const art = currentArt(), key = S.art ? 'a' + S.artVersion : 's' + S.artVersion;
      if (artPts.key === key) return artPts.pts;
      const n = 64, c = makeCanvas(n, n), x = c.getContext('2d');
      x.drawImage(art, 0, 0, n, n);
      const d = x.getImageData(0, 0, n, n).data, pts = [];
      for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) if (d[(j * n + i) * 4 + 3] > 64) pts.push([(i + 0.5) / n - 0.5, (j + 0.5) / n - 0.5]);
      artPts = { key, pts };
      return pts;
    }

    // Biggest width that still fits the hat's front panel box (maxW x maxH inches), so a
    // wide logo stops at the panel width and a tall one stops at the panel height.
    // minIn applies to the patch's longer side, so a tall narrow logo can still shrink.
    function minWidthIn() { return C.minIn / Math.max(1, aspect()); }
    function maxWidthIn() { return Math.max(minWidthIn(), Math.min(C.maxW, C.maxH / aspect())); }
    function fitSize(w) { return clamp(w, minWidthIn(), maxWidthIn()); }

    function pxPerInch(W) { return W / C.inchesAcross; }

    // Pixel layout of the flat patch and where the logo sits inside it.
    function layout(W) {
      const inch = patchInches();
      const k = pxPerInch(W);
      const look = deco().look;
      const art = currentArt();
      let pw = inch.w * k, ph = inch.h * k;
      let pad = 0, box;
      if (look === 'embroidery') {
        box = { x: 0, y: 0, w: pw, h: ph };
      } else if (S.shape === 'diecut') {
        pad = borderIn() * k; // may be 0: logo edge = cut line
        const aw = pw - pad * 2, ah = ph - pad * 2;
        box = { x: pad, y: pad, w: aw, h: ah };
      } else {
        // Fit the logo inside the shape, keeping `border` inches clear of the edge.
        const m = borderIn() * k, ar = art.width / art.height;
        let bw = Math.max(1, pw - 2 * m), bh = bw / ar;
        if (bh > ph - 2 * m) { bh = Math.max(1, ph - 2 * m); bw = bh * ar; }
        if (S.shape === 'circle' || S.shape === 'oval' || S.shape === 'hex') {
          // Every visible logo pixel must sit inside the ellipse (hexagon: its inscribed ellipse).
          const a = (pw / 2) * (S.shape === 'hex' ? 0.86 : 1) - m, b = ph / 2 - m;
          let worst = 0;
          for (const [u, v] of artPoints()) worst = Math.max(worst, Math.hypot(u / a, v / (ar * b)));
          if (worst > 0 && a > 0 && b > 0) { bw = Math.min(bw, 1 / worst); bh = bw / ar; }
        }
        box = { x: (pw - bw) / 2, y: (ph - bh) / 2, w: bw, h: bh };
      }
      return { pw: Math.round(pw), ph: Math.round(ph), pad, box, bridge: 0.1 * k };
    }

    function patchCanvas(W) {
      const L = layout(W);
      return buildPatch({
        look: deco().look,
        shape: S.shape,
        W: L.pw, H: L.ph,
        box: L.box, pad: L.pad, bridge: L.bridge,
        art: currentArt(),
        mask: maskFor(),
        reverse: S.reverse && !(S.art && S.twoTone), // two-tone logos already swapped in the mask
        color: patchColor(),
        base: deco().look === 'uv' ? cfg.uvBases[S.uvBaseIdx].hex : '#ffffff',
      });
    }

    /* --- render --- */

    let patchCache = { key: '', canvas: null };
    // While a slider / pinch / wheel is moving, rebuilding a die-cut patch every step is too
    // slow on phones. Stretch the last drawing instead and rebuild once things settle.
    let settleTimer = 0;
    function interacting() {
      S.interacting = true;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => { S.interacting = false; render(); }, 160);
    }

    function cachedPatch(W) {
      const key = [W, S.decoIdx, S.patchIdx, S.shape, S.wIn.toFixed(3), S.artVersion, S.reverse, S.uvBaseIdx, borderIn().toFixed(3)].join('|');
      if (patchCache.key !== key) {
        if (S.interacting && patchCache.canvas && patchCache.W === W) {
          const L = layout(W), c = makeCanvas(L.pw, L.ph);
          c.getContext('2d').drawImage(patchCache.canvas, 0, 0, c.width, c.height);
          return c;
        }
        patchCache = { key, W, canvas: patchCanvas(W) };
      }
      return patchCache.canvas;
    }

    function drawScene(target, W) {
      const H = W;
      const x = target.getContext('2d');
      target.width = W; target.height = H;
      x.fillStyle = '#fff';
      x.fillRect(0, 0, W, H);
      if (!S.hat) return null;
      const cr = P.photo.crop, iw = S.hat.naturalWidth, ih = S.hat.naturalHeight;
      x.drawImage(S.hat, cr.x * iw, cr.y * ih, cr.w * iw, cr.h * ih, 0, 0, W, H);

      const patch = target === canvas ? cachedPatch(W) : patchCanvas(W);
      const cx = (S.x / 100) * W, cy = (S.y / 100) * H;
      const rot = (S.rot * Math.PI) / 180;
      const look = deco().look;
      const R = (C.crownR || 0.4) * W;
      const wrap = { R, yaw: Math.asin(clamp((cx - (C.centerX || 50) / 100 * W) / R, -0.85, 0.85)), drop: C.curve };

      // Contact shadow: soft, darker under the patch, lets the hat show through at the edge.
      x.save();
      x.globalCompositeOperation = 'multiply';
      x.globalAlpha = look === 'embroidery' ? 0.25 : 0.4;
      x.filter = `blur(${Math.max(1.5, patch.width * (look === 'embroidery' ? 0.008 : 0.018))}px)`;
      drawWrapped(x, silhouette(patch, '#1d232b'), cx + patch.width * 0.008, cy + patch.width * 0.022, rot, wrap);
      x.restore();
      drawWrapped(x, patch, cx, cy, rot, wrap);
      return { cx, cy, w: patch.width * Math.cos(wrap.yaw), h: patch.height, rot };
    }

    let lastBox = null, frame = 0;
    function render() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { lastBox = drawScene(canvas, 1000); });
    }

    /* --- sync UI with state --- */

    // Price for the current selection. Per-hat price is the decoration's variant price, less
    // the quantity break (charged at checkout by automatic discounts); embroidery adds a
    // one-time setup unless it's a reorder.
    function pricing() {
      const d = deco(), breaks = P.breaks || [];
      // On Shopify the per-hat price comes from the real variant (color x decoration).
      // Patch colour is passed too: on some listings (YP, straw) the patch colour IS the variant.
      const v = cfg.variantFor ? cfg.variantFor(P.colors[S.colorIdx].name, d.name, patchColor() && patchColor().name) : null;
      const base = v ? v.price : d.price;
      let off = 0, tier = 0, next = null;
      for (const b of breaks) { if (S.qty >= b.qty) { off = b.off; tier = b.qty; } else if (!next) next = b; }
      const each = base - off, setup = d.setup && !S.reorder ? d.setup : 0;
      return { base, each, off, tier, next, setup, total: each * S.qty + setup, variant: v };
    }
    const minQty = () => deco().minQty || 1;

    function syncUI() {
      const d = deco();
      root.querySelectorAll('[data-color]').forEach((b) => b.classList.toggle('is-on', +b.dataset.color === S.colorIdx));
      root.querySelectorAll('[data-deco]').forEach((b) => b.classList.toggle('is-on', +b.dataset.deco === S.decoIdx));
      root.querySelectorAll('[data-patch]').forEach((b) => b.classList.toggle('is-on', +b.dataset.patch === S.patchIdx));
      root.querySelectorAll('[data-uvbase]').forEach((b) => b.classList.toggle('is-on', +b.dataset.uvbase === S.uvBaseIdx));
      root.querySelectorAll('[data-shape]').forEach((b) => { b.hidden = !shapeOk(b.dataset.shape); });
      $('[data-shape-note]').textContent = d.shapeNote || '';
      $('[data-shape-note]').hidden = !d.shapeNote;
      root.querySelectorAll('[data-shape]').forEach((b) => b.classList.toggle('is-on', b.dataset.shape === S.shape));
      root.querySelectorAll('[data-preset]').forEach((b) => b.classList.toggle('is-on', b.dataset.preset === S.preset));
      $('[data-color-name]').textContent = P.colors[S.colorIdx].name;
      $('[data-patch-name]').textContent = patchColor().name;
      $('[data-patch-group]').hidden = d.look !== 'leather';
      $('[data-shape-group]').hidden = d.look === 'embroidery';
      $('[data-uvbase-group]').hidden = d.look !== 'uv';
      $('[data-invert-row]').hidden = d.look !== 'leather';
      $('[data-bg-row]').hidden = !S.raw;
      $('.sthc-approx').hidden = !d.approximate;

      S.wIn = fitSize(S.wIn);
      const bd = $('[data-border]'), noBorder = d.look === 'embroidery';
      $('[data-border-row]').hidden = noBorder;
      $('[data-border-note]').hidden = noBorder;
      bd.min = 0;
      bd.max = Math.min(0.5, S.wIn / 4).toFixed(2);
      bd.value = borderIn();
      $('[data-border-val]').textContent = borderIn().toFixed(2) + ' in';
      $('[data-border-note]').textContent = S.shape === 'diecut' ? 'Border of patch material around your logo outline.' : 'Space between your logo and the edge of the patch.';
      size.min = minWidthIn();
      size.max = maxWidthIn();
      const inch = patchInches();
      size.value = S.wIn;
      $('[data-size-val]').textContent = `${inch.w.toFixed(2)} × ${inch.h.toFixed(2)} in`;
      const atMax = S.wIn >= maxWidthIn() - 0.01;
      const lim = $('[data-size-note]');
      lim.hidden = !atMax;
      lim.textContent = atMax ? `That's the largest that fits the front of a ${P.style} (${C.maxW}" wide × ${C.maxH}" tall).` : '';
      $('[data-x]').value = S.x; $('[data-x-val]').textContent = Math.round(S.x) + '%';
      $('[data-y]').value = S.y; $('[data-y-val]').textContent = Math.round(S.y) + '%';
      $('[data-rot]').value = S.rot; $('[data-rot-val]').textContent = S.rot + '°';

      const pr = pricing();
      $('[data-qty]').value = S.qty;
      $('[data-total]').textContent = money(pr.total);
      $('[data-each]').textContent = (S.qty > 1 ? `${money(pr.each)} each` : '') + (pr.off ? ` · ${pr.tier}+ price` : '');
      $('[data-fees]').hidden = !d.setup;
      $('[data-setup-line]').textContent = d.setup ? (S.reorder ? 'Embroidery setup waived (reorder).' : `Includes a one-time ${money(d.setup)} embroidery setup (we turn your logo into a stitch file).`) : '';
      $('[data-reorder]').checked = !!S.reorder;
      const mn = $('[data-min-note]');
      mn.hidden = !d.minQty;
      mn.textContent = d.minQty ? `${d.name}: ${d.minQty} hat minimum because of setup.` : '';
      const nb = pr.next;
      $('[data-next-break]').textContent = nb ? `Order ${nb.qty} or more and they're ${money(pr.base - nb.off)} each.` : '';
      $('[data-add]').disabled = !!(pr.variant && !pr.variant.available);
      $('[data-add]').textContent = pr.variant && !pr.variant.available ? 'Sold out in this color' : 'Add to cart';
      const bulk = cfg.bulk;
      $('[data-bulk]').innerHTML = bulk ? (S.qty >= bulk.minQty
        ? `Ordering ${bulk.minQty}+? <a href="${esc(bulk.url)}">Get bulk pricing with this design →</a>`
        : `Need ${bulk.minQty}+ for your crew? <a href="${esc(bulk.url)}">Bulk pricing →</a>`) : '';

      const st = $('[data-status]');
      const up = S.upload;
      if (S.file) {
        st.hidden = false;
        let msg = esc(S.fileName);
        if (!S.previewable) msg += ' · We can\'t preview this file type here, so the hat shows a placeholder. We\'ll send you a proof.';
        else if (S.bgRemoved) msg += ' · Background removed.';
        if (up && up.state === 'uploading') msg += ' · Uploading…';
        if (up && up.state === 'done') msg += ' · Saved ✓';
        if (up && up.state === 'error') msg += ' · Upload failed: ' + esc(up.error);
        st.innerHTML = msg;
        st.classList.toggle('is-error', !!(up && up.state === 'error'));
      } else st.hidden = true;
      $('[data-drop-title]').textContent = S.file ? 'Replace your logo' : 'Drop your logo here or choose a file';
    }

    function update() { syncUI(); render(); }

    /* --- hat photo --- */

    async function loadHat() {
      const c = P.colors[S.colorIdx];
      const url = P.photo.url.replace('{id}', c.id);
      $('.sthc-loading').hidden = false;
      try {
        S.hat = await loadImage(url);
      } catch (e) {
        showError('Could not load that hat photo. Try another color.');
      }
      $('.sthc-loading').hidden = true;
      render();
    }

    /* --- upload --- */

    async function upload(file, name) {
      if (!cfg.uploadUrl) return null;
      const r = await fetch(cfg.uploadUrl, {
        method: 'POST',
        headers: { 'X-File-Name': encodeURIComponent(name || file.name) },
        body: file,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'upload failed (' + r.status + ')');
      return j.url;
    }

    async function takeFile(file) {
      if (!file) return;
      hideError();
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!cfg.acceptExt.includes(ext)) { showError('That file type isn\'t supported. Use ' + cfg.acceptExt.join(', ').toUpperCase() + '.'); return; }
      if (file.size > (cfg.maxMB || 10) * 1048576) { showError(`Files need to be under ${cfg.maxMB || 10} MB. Email bigger files to us.`); return; }
      S.file = file;
      S.fileName = file.name;
      S.previewable = PREVIEWABLE.includes(ext);
      S.emailLater = false;
      $('[data-later]').checked = false;
      if (S.previewable) {
        const url = URL.createObjectURL(file);
        try {
          S.raw = artToCanvas(await loadImage(url));
        } catch (e) {
          S.raw = null;
          S.previewable = false;
        }
        URL.revokeObjectURL(url);
      } else {
        S.raw = null;
      }
      if (!S.previewable) { S.sample = sampleArt('PROOF', 'TO FOLLOW'); sampleMask = null; }
      S.artVersion++;
      processArt();
      S.upload = { state: cfg.uploadUrl ? 'uploading' : 'idle' };
      update();
      if (cfg.uploadUrl) {
        try {
          const u = await upload(file);
          if (S.file === file) S.upload = { state: 'done', url: u };
        } catch (e) {
          if (S.file === file) S.upload = { state: 'error', error: e.message };
        }
        syncUI();
      }
    }

    /* --- errors --- */

    function showError(t) { const e = $('[data-error]'); e.textContent = t; e.hidden = false; }
    function hideError() { $('[data-error]').hidden = true; }

    /* --- events: options --- */

    root.addEventListener('click', (ev) => {
      const b = ev.target.closest('button');
      if (!b || !root.contains(b)) return;
      if (b.dataset.color != null) { S.colorIdx = +b.dataset.color; update(); loadHat(); }
      else if (b.dataset.deco != null) {
        S.decoIdx = +b.dataset.deco;
        // A minimum (e.g. 12 for sublimated) raises the quantity; if the customer only looked at
        // that option and came back, put their quantity back rather than leave them at 12.
        if (S.qty < minQty()) { if (S.qtyBeforeMin == null) S.qtyBeforeMin = S.qty; S.qty = minQty(); }
        else if (S.qtyBeforeMin != null && minQty() <= S.qtyBeforeMin) { S.qty = S.qtyBeforeMin; S.qtyBeforeMin = null; }
        else if (S.qtyBeforeMin != null && minQty() === 1) { S.qty = S.qtyBeforeMin; S.qtyBeforeMin = null; }
        fixShape();
        patchCache.key = ''; update();
      }
      else if (b.dataset.patch != null) { S.patchIdx = +b.dataset.patch; update(); }
      else if (b.dataset.uvbase != null) { S.uvBaseIdx = +b.dataset.uvbase; update(); }
      else if (b.dataset.shape != null) {
        S.shape = b.dataset.shape;
        S.borderIn = null;
        S.wIn = fitSize(shapeDef().defaultIn);
        update();
      } else if (b.dataset.preset != null) {
        const p = P.presets.find((q) => q.name === b.dataset.preset);
        S.preset = p.name; S.x = p.x; S.y = p.y; S.rot = 0;
        update();
      } else if (b.dataset.step != null) {
        S.qty = clamp(S.qty + +b.dataset.step, minQty(), 999); S.qtyBeforeMin = null; syncUI();
      } else if (b.hasAttribute('data-download')) download();
      else if (b.hasAttribute('data-reset')) {
        const p = P.presets[0];
        S.preset = p.name; S.x = p.x; S.y = p.y; S.rot = 0;
        S.wIn = fitSize(shapeDef().defaultIn);
        update();
      } else if (b.hasAttribute('data-add')) addToCart(b);
    });

    const custom = () => { S.preset = 'Custom'; };
    size.addEventListener('input', () => { interacting(); S.wIn = +size.value; update(); });
    $('[data-border]').addEventListener('input', (e) => { interacting(); S.borderIn = +e.target.value; update(); });
    $('[data-x]').addEventListener('input', (e) => { S.x = +e.target.value; custom(); update(); });
    $('[data-y]').addEventListener('input', (e) => { S.y = +e.target.value; custom(); update(); });
    $('[data-rot]').addEventListener('input', (e) => { S.rot = +e.target.value; custom(); update(); });
    $('[data-qty]').addEventListener('change', (e) => { S.qty = clamp(parseInt(e.target.value, 10) || 1, minQty(), 999); S.qtyBeforeMin = null; syncUI(); });
    $('[data-reorder]').addEventListener('change', (e) => { S.reorder = e.target.checked; syncUI(); });
    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-place]')) {
        S.places = [...root.querySelectorAll('[data-place]:checked')].map((c) => P.placements[+c.dataset.place]);
      }
    });
    $('[data-removebg]').addEventListener('change', (e) => { S.removeBg = e.target.checked; S.artVersion++; processArt(); update(); });
    $('[data-invert]').addEventListener('change', (e) => { S.reverse = e.target.checked; S.artVersion++; processArt(); update(); });
    $('[data-later]').addEventListener('change', (e) => { S.emailLater = e.target.checked; hideError(); });

    const fileInput = $('[data-file]'), drop = $('[data-drop]');
    fileInput.addEventListener('change', () => { takeFile(fileInput.files[0]); fileInput.value = ''; });
    ['dragenter', 'dragover'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach((t) => drop.addEventListener(t, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
    drop.addEventListener('drop', (e) => takeFile(e.dataTransfer.files[0]));

    /* --- events: drag, wheel and pinch on the hat --- */

    function toCanvas(ev) {
      const r = canvas.getBoundingClientRect();
      return { x: ((ev.clientX - r.left) / r.width) * canvas.width, y: ((ev.clientY - r.top) / r.height) * canvas.height };
    }
    function onPatch(p) {
      if (!lastBox) return false;
      const dx = p.x - lastBox.cx, dy = p.y - lastBox.cy;
      const c = Math.cos(-lastBox.rot), s = Math.sin(-lastBox.rot);
      const lx = dx * c - dy * s, ly = dx * s + dy * c;
      return Math.abs(lx) <= lastBox.w / 2 + 12 && Math.abs(ly) <= lastBox.h / 2 + 12;
    }

    const pointers = new Map();
    let drag = null, pinch = null;
    canvas.addEventListener('pointerdown', (ev) => {
      const p = toCanvas(ev);
      pointers.set(ev.pointerId, p);
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), w: S.wIn };
        drag = null;
        return;
      }
      if (!onPatch(p)) return;
      canvas.setPointerCapture(ev.pointerId);
      drag = { px: p.x, py: p.y, x: S.x, y: S.y };
      canvas.classList.add('is-dragging');
      ev.preventDefault();
    });
    canvas.addEventListener('pointermove', (ev) => {
      const p = toCanvas(ev);
      if (pointers.has(ev.pointerId)) pointers.set(ev.pointerId, p);
      if (pinch && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        interacting();
        S.wIn = fitSize(pinch.w * (d / pinch.d));
        update();
        return;
      }
      if (drag) {
        S.x = clamp(drag.x + ((p.x - drag.px) / canvas.width) * 100, 20, 80);
        S.y = clamp(drag.y + ((p.y - drag.py) / canvas.height) * 100, C.yMin ?? 25, C.yMax ?? 70);   // beanies sit low, on the cuff
        custom();
        update();
      } else {
        canvas.classList.toggle('is-over-patch', onPatch(p));
      }
    });
    const end = (ev) => {
      pointers.delete(ev.pointerId);
      if (pointers.size < 2) pinch = null;
      drag = null;
      canvas.classList.remove('is-dragging');
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    // On phones the hat is most of the screen, so only take over a touch that starts on
    // the patch (drag) or uses two fingers (pinch); any other swipe scrolls the page.
    canvas.addEventListener('touchstart', (ev) => {
      if (ev.touches.length > 1 || onPatch(toCanvas(ev.touches[0]))) ev.preventDefault();
    }, { passive: false });
    canvas.addEventListener('wheel', (ev) => {
      if (!onPatch(toCanvas(ev))) return; // let the page scroll unless they're on the patch
      ev.preventDefault();
      interacting();
      S.wIn = fitSize(S.wIn * (ev.deltaY < 0 ? 1.04 : 1 / 1.04));
      update();
    }, { passive: false });

    /* --- export + cart --- */

    function mockupBlob() {
      return new Promise((resolve) => {
        const c = makeCanvas(1200, 1200);
        drawScene(c, 1200);
        c.toBlob(resolve, 'image/png');
      });
    }

    function fileStem() {
      return ('sth-' + P.style + '-' + P.colors[S.colorIdx].name).toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    async function download() {
      const blob = await mockupBlob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileStem() + '-mockup.png';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    }

    // Same property names TEPO used, so order handling downstream keeps working.
    function properties(mockupUrl) {
      const d = deco(), inch = patchInches();
      const props = {};
      if (d.look === 'leather') props['Leather Patch Color'] = patchColor().name;
      if (d.look === 'uv') props['Patch Material'] = cfg.uvBases[S.uvBaseIdx].name;
      props['Patch Placement'] = S.preset;
      props['Logo Upload'] = S.emailLater && !S.file ? 'Customer will email logo' : (S.upload && S.upload.url) || S.fileName;
      if (mockupUrl) props['Mockup'] = mockupUrl;
      if (d.look !== 'embroidery') props['Patch Shape'] = shapeDef().label;
      props['Size'] = `${inch.w.toFixed(2)} x ${inch.h.toFixed(2)} in`;
      if (d.look !== 'embroidery') props['Border'] = borderIn().toFixed(2) + ' in';
      if (d.look === 'leather') props['Engraving'] = S.reverse ? (S.art && S.twoTone ? 'Reversed (tones swapped)' : 'Reversed (background engraved)') : 'Standard';
      if (deco().setup && S.reorder) props['Reorder'] = 'Yes, setup fee waived (check past orders)';
      if (S.places && S.places.length) props['Extra placements'] = S.places.join(', ') + ' (price confirmed with proof)';
      props['_Placement'] = `x ${S.x.toFixed(1)}% y ${S.y.toFixed(1)}% rot ${S.rot}°`;
      props['_Source'] = 'STH Customizer';
      return props;
    }

    async function addToCart(btn) {
      hideError();
      if (!S.file && !S.emailLater) { showError('Add your logo, or tick "I\'ll email my logo after ordering".'); return; }
      if (S.upload && S.upload.state === 'uploading') { showError('Your logo is still uploading. One second…'); return; }
      if (S.upload && S.upload.state === 'error') { showError('Your logo didn\'t upload. Try again, or tick "I\'ll email my logo".'); return; }
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Adding…';
      try {
        let mockupUrl = '';
        if (cfg.uploadUrl) {
          const blob = await mockupBlob();
          mockupUrl = await upload(blob, fileStem() + '-mockup.png');
        }
        const pr = pricing();
        const payload = {
          variantId: pr.variant ? pr.variant.id : null,
          color: P.colors[S.colorIdx].name,
          decoration: deco().name,
          quantity: S.qty,
          properties: properties(mockupUrl),
          // One-time setup goes on its own cart line (a $15 "Embroidery setup" product).
          setupFee: pricing().setup ? { amount: pricing().setup, title: 'Embroidery setup (digitizing)', logo: S.fileName || 'emailed logo' } : null,
          expectedTotal: pricing().total,
        };
        await cfg.onAddToCart(payload);
        btn.textContent = 'Added ✓';
        setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 1600);
      } catch (e) {
        showError('Something went wrong: ' + e.message);
        btn.textContent = label;
        btn.disabled = false;
      }
    }

    texListeners.add(() => { patchCache.key = ''; render(); });

    // Start-up
    update();
    loadHat();

    return { state: S, render, properties, mockupBlob };
  }

  // A standalone engraved patch for swatch boards / admin pages.
  let swatchArt = null, swatchMask = null;
  function renderSwatch(color, W, opts) {
    opts = opts || {};
    if (!swatchArt) { swatchArt = sampleArt('YOUR', 'LOGO HERE'); swatchMask = engraveMask(swatchArt, false).canvas; }
    const H = Math.round(W * 0.667), ar = swatchArt.width / swatchArt.height;
    let bw = W * 0.78, bh = bw / ar;
    if (bh > H * 0.78) { bh = H * 0.78; bw = bh * ar; }
    return buildPatch({ look: 'leather', shape: opts.shape || 'rounded', W, H, box: { x: (W - bw) / 2, y: (H - bh) / 2, w: bw, h: bh },
      pad: 0, art: swatchArt, mask: swatchMask, color, reverse: !!opts.reverse });
  }

  window.STHCustomizer = {
    mount, renderSwatch, onTexture: (cb) => texListeners.add(cb),
    // Shared with the apparel builder (apparel.js).
    util: { clamp, money, esc, makeCanvas, loadImage, artToCanvas, removeBackground, trim, sampleArt, PREVIEWABLE },
  };
})();
